import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';

import { MenuOption, OptionGroup, RdataPipe, ResolveLibModule } from 'resolve-lib';
import { firstValueFrom, map, Observable, of, Subscription } from 'rxjs';
import {
  ListRDataItemSortBy,
  PositionType,
  PracticeType,
  PracticeUserViewModel,
  RDataPracticeViewModelPaginatedItemsViewModel,
  SortOrder,
} from '../../api/models';
import { RDataService as SpecialtyService } from '../../api/services/r-data.service';
import { GroupedSelectMenuComponent } from '../../components/grouped-select-menu/grouped-select-menu.component';
import { MultiselectComponent } from '../../components/multiselect/multiselect.component';
import { PageHeadingComponent } from '../../components/page-heading/page-heading.component';
import { PageWellComponent } from '../../components/page-well/page-well.component';
import { States } from '../../models/states-enum';
import { RDataService } from '../../services/rdata.service';
import { SearchRequestService } from '../../services/search-request/search-request.service';
import { NoAccessComponent } from '../../components/no-access/no-access.component';
import { UserService } from '../../services/user.service';
import { RdataRecordsRequest } from '../../models/rdata-records-request';

@Component({
    selector: 'app-rdata-records-page',
    templateUrl: './rdata-records.component.html',
    styleUrls: ['./rdata-records.component.scss'],
    imports: [
        CommonModule,
        PageHeadingComponent,
        ResolveLibModule,
        PageWellComponent,
        MultiselectComponent,
        GroupedSelectMenuComponent,
    ]
})
export class RDataRecordsPage {
  #dataSubscriptions: Subscription = new Subscription();
  #rDataPipe = inject(RdataPipe);

  rDataRecords$: Observable<RDataPracticeViewModelPaginatedItemsViewModel>;

  userSpecialties = signal<Array<OptionGroup> | null>(null);
  showFilters = signal<boolean>(false);

  userSpecialties$: Observable<OptionGroup[]> | undefined;
  userStates$: Observable<OptionGroup[]> | undefined;
  positionTypes$: Observable<OptionGroup[]> | undefined;
  practiceTypes$: Observable<OptionGroup[]> | undefined;
  disabledSpecialties$: Observable<OptionGroup[]> | undefined;
  disabledStates$: Observable<OptionGroup[]> | undefined;
  allowedRegionOptions$: Observable<MenuOption[]>;
  userLoaded: Promise<boolean>;

  specialties: MenuOption[] = [];
  isVerified: boolean | null = null;
  states: MenuOption[] = [];
  positionType: MenuOption | null = null;
  practiceTypes: MenuOption[] = [];
  pageSize: number = 10;
  pageNumber: number = 1;
  sortBy: ListRDataItemSortBy | null = null;
  sortOrder: SortOrder | null = null;

  constructor(
    private rDataService: RDataService,
    private specialtyService: SpecialtyService,
    private searchRequestService: SearchRequestService,
    private userService: UserService,
  ) {}

  ngOnInit() {
    this.setOptionGroups();
  }

  getRecordData() {
    const specialties = (this.specialties ?? []).map((s) => s.value);
    const states = (this.states ?? []).map((s) => s.value);
    const practiceTypes = (this.practiceTypes ?? []).map((p) => p.value);
    const positionTypes = this.positionType?.value as PositionType;

    const rDataRecordsRequest = {
      Specialties: specialties,
      Academic: null,
      Verified: this.isVerified,
      StateAbbreviations: states,
      PositionType: positionTypes,
      PracticeTypes: practiceTypes,
      Region: null,
      PageSize: this.pageSize,
      PageNumber: this.pageNumber,
      SortBy: this.sortBy,
      SortOrder: this.sortOrder
    }

    this.rDataRecords$ = this.#getRdataRecords(rDataRecordsRequest);
    
    //Save the search request object in the service signal to retrieve later if navigating back.
    this.searchRequestService.saveRdataRecordsRequest(rDataRecordsRequest);
  }

  loadPreviousSearch(): void {
    const rDataRecordsRequest = this.searchRequestService.getRDataRecordsRequest();
    this.rDataRecords$ = this.#getRdataRecords(rDataRecordsRequest);
  }

  #getRdataRecords(rDataRecordsRequest: RdataRecordsRequest): Observable<RDataPracticeViewModelPaginatedItemsViewModel | null> {
    return this.rDataService.getRDataRecordsResponse(rDataRecordsRequest).pipe(
      map(response => {
        if(response.ok){
          return response.body;
        }
        else if(response.status === 403){
          this.userService.hasAccess.set(false);
          return null;
        }
        else {
          return null;
        }
      })
    );  
  }

  setOptionGroups() {
    const userDetails$ = this.rDataService.getUserDetails();
    this.userLoaded = new Promise((resolve) => {
      this.#dataSubscriptions.add(
        userDetails$.subscribe(async (details: PracticeUserViewModel) => {
          if(details.isActive){        
            const specialtyGroup = {
              name: 'Specialties',
              options: (details?.specialties.sort().map((specialty) => ({
                label: specialty,
                value: specialty,
              })) ?? []) as MenuOption[],
            } as OptionGroup;
            const stateGroup = {
              name: 'States',
              options: (details?.states.map((state) => ({
                label: this.getFullStateName(state),
                value: state,
              })) ?? []) as MenuOption[],
            } as OptionGroup;
            const practiceTypeGroup = {
              name: 'Practice Types',
              options: Object.values(PracticeType).map((type) => ({
                label: this.#rDataPipe.transform(type, 'practiceType'),
                value: type,
              })) as MenuOption[],
            } as OptionGroup;
            const positionTypeGroup = {
              name: 'Position Types',
              options: Object.values(PositionType).map((type) => ({
                label: this.#rDataPipe.transform(type, 'positionType'),
                value: type,
              })) as MenuOption[],
            } as OptionGroup;

            const allSpecialties = (await this.getAllSpecialties()) ?? [];
            const allStates = this.getAllStates() ?? [];

            const disabledSpecialtyGroup = {
              name: 'Disabled Specialties',
              options: allSpecialties
                .filter(
                  (s: string) =>
                    !specialtyGroup.options.some((o) => o.value === s)
                )
                .map((s: string) => ({ label: s, value: s } as MenuOption)),
            };
            const disabledStateGroup = {
              name: 'Disabled States',
              options: allStates
                .filter(
                  (s: string) => !stateGroup.options.some((o) => o.value === s)
                )
                .map(
                  (s: string) =>
                    ({ label: this.getFullStateName(s), value: s } as MenuOption)
                ),
            };

            this.userSpecialties$ = of([specialtyGroup]);
            this.userStates$ = of([stateGroup]);
            this.practiceTypes$ = of([practiceTypeGroup]);
            this.positionTypes$ = of([positionTypeGroup]);

            this.disabledSpecialties$ = of([disabledSpecialtyGroup]);
            this.disabledStates$ = of([disabledStateGroup]);


          if(this.searchRequestService.getRDataRecordsRequest() !== null){
            //if we have a previous search (i.e. user navigated back to this page) fill out the selected filters and then send the request.
            const request = this.searchRequestService.getRDataRecordsRequest();
            this.specialties = specialtyGroup.options.filter(option => request.Specialties.includes(option.value));
            this.isVerified = request.Verified;
            this.states = stateGroup.options.filter(option => request.StateAbbreviations.includes(option.value));
            this.positionType = positionTypeGroup.options.find(option => request.PositionType === option.value);
            this.practiceTypes = practiceTypeGroup.options.filter(option => request.PracticeTypes.includes(option.value));
            this.pageSize = request.PageSize;
            this.pageNumber = request.PageNumber;
            this.sortBy = request.SortBy;
            this.sortOrder = request.SortOrder;

            this.loadPreviousSearch();
          }
          else {
            //otherwise there is no search saved already so just proceed as you normally would.
            this.getRecordData();
          }

            resolve(true);
          }
          else {
            this.userService.hasAccess.set(false);

            resolve(true);
          }
        })
      );
    });
  }

  selectSpecialty(specialtyOptions: MenuOption[]) {
    this.specialties = specialtyOptions ?? [];
    this.pageNumber = 1;
    this.getRecordData();
  }

  selectState(stateOptions: MenuOption[]) {
    this.states = stateOptions ?? [];
    this.pageNumber = 1;
    this.getRecordData();
  }

  selectPracticeType(practiceTypeOptions: MenuOption[]) {
    this.practiceTypes = practiceTypeOptions ?? [];
    this.pageNumber = 1;
    this.getRecordData();
  }

  selectPositionType(positionType: MenuOption) {
    this.positionType = positionType ?? null;
    this.pageNumber = 1;
    this.getRecordData();
  }

  toggleVerified() {
    if (this.isVerified === null) {
      this.isVerified = true;
    } else {
      this.isVerified = null;
    }

    this.pageNumber = 1;
    this.getRecordData();
  }

  toggleFilters() {
    this.showFilters.set(!this.showFilters());
  }

  onSortChanged(sortOptions: string[]) {
    this.sortOrder = sortOptions.at(0) as SortOrder;
    this.sortBy = sortOptions.at(1) as ListRDataItemSortBy;
    this.pageNumber = 1;
    this.getRecordData();
  }

  parseStats(stat) {
    return stat ?? '-';
  }

  parsePto(stat): string {
    return Math.round(stat / 40).toString() ?? null;
  }

  ngOnDestroy() {
    this.#dataSubscriptions.unsubscribe();
  }

  onPageChanged(pageNumber: number) {
    this.pageNumber = pageNumber;
    this.getRecordData();
  }

  async getAllSpecialties(): Promise<string[]> {
    return await firstValueFrom(
      this.specialtyService
        .compensationApiRdataAggregatesSpecialtiesGet$Json$Response({
          'api-version': '1',
        })
        .pipe(map((response) => response.body ?? []))
    );
  }

  getAllStates(): string[] {
    return Object.values(States);
  }

  getFullStateName(abbreviation: string): string {
    const stateName = Object.entries(States).reduce((acc, val) =>
      acc[1] === abbreviation ? acc : val
    )[0];
    return this.#parseStateName(stateName);
  }

  #parseStateName(stateName: string) {
    return stateName.replace(/_/g, ' ');
  }
}
